// Port of ios/Sources/ViewModels/TeammateNameDisplay.swift.
//
// How a person's name is rendered, per the server's teammate-name-display policy — the same
// rule the webapp's `displayUsername` applies, so a name never reads differently here than on
// the desktop (shared/API.md §13.4, §13.5). A decision-for-decision port of Android's
// `TeammateNameDisplay`, tests included.
//
// WHY THIS EXISTS AT ALL. The app used to hardcode an order, and every order is wrong for
// someone: this deployment's people put their DEPARTMENT in the `nickname` field (a real user
// with first name "Ross" carries nickname "Relaciones laborales"), so nickname-first titled
// every DM with her after her department while the desktop showed "Ross" — because HER
// READER's preference is `full_name`. The policy is a property of the reader, not of the
// person being named, and only the server can state it.
//
// Usage: `import * as TeammateNameDisplay from './teammateNameDisplay'` reads like the Swift enum.
import type { UserRecord } from './records';

/**
 * The three policies Mattermost defines, plus "nothing has synced yet".
 * - `username` — always the @handle.
 * - `nicknameFirst` — `nickname_full_name`: nickname, else full name, else username.
 * - `fullName` — `full_name`: full name, else username.
 * - `unresolved` — no policy known (first launch, before the first sync answers). Falls back to
 *   full name > nickname > username, which is what this deployment's people expect and never
 *   renders a bare handle for someone who filled their profile in.
 */
export type Format = 'username' | 'nicknameFirst' | 'fullName' | 'unresolved';

/** Swift's `.whitespaces` (spaces and tabs — NOT newlines). */
function trimSpaces(s: string): string {
  return s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
}

/**
 * Wire value → Format, or null for absent/unrecognised — a value we do not know is not a
 * policy we can honour, and guessing one is how the department-in-the-nickname bug happened.
 */
export function parse(value: string | null | undefined): Format | null {
  if (value == null) return null;
  switch (trimSpaces(value)) {
    case 'username':
      return 'username';
    case 'nickname_full_name':
      return 'nicknameFirst';
    case 'full_name':
      return 'fullName';
    default:
      return null;
  }
}

/**
 * The effective policy. `LockTeammateNameDisplay = true` means the admin took the choice
 * away, so the server default wins over any preference still stored on the account;
 * otherwise the user's own `display_settings/name_format` wins, and the server default is
 * the fallback for an account that never set one.
 */
export function resolveFormat(
  serverDefault: string | null | undefined,
  serverLocked: string | null | undefined,
  userPreference: string | null | undefined,
): Format {
  const locked = serverLocked != null && trimSpaces(serverLocked).toLowerCase() === 'true';
  const defaultFormat = parse(serverDefault);
  if (locked && defaultFormat) return defaultFormat;
  const preferred = parse(userPreference);
  if (preferred) return preferred;
  return defaultFormat ?? 'unresolved';
}

/** "First Last", empty when the user has filled in neither. */
export function fullName(user: Pick<UserRecord, 'firstName' | 'lastName'>): string {
  const first = trimSpaces(user.firstName ?? '');
  const last = trimSpaces(user.lastName ?? '');
  if (first === '') return last;
  if (last === '') return first;
  return first + ' ' + last;
}

/**
 * The name to show under `format`. Never empty: `username` is always populated on a
 * Mattermost account and is the last resort in every branch, as on the webapp.
 */
export function name(
  user: Pick<UserRecord, 'username' | 'nickname' | 'firstName' | 'lastName'>,
  format: Format,
): string {
  const full = fullName(user);
  const nickname = trimSpaces(user.nickname ?? '');
  switch (format) {
    case 'username':
      return user.username;
    case 'nicknameFirst':
      if (nickname !== '') return nickname;
      if (full !== '') return full;
      return user.username;
    case 'fullName':
      return full === '' ? user.username : full;
    case 'unresolved':
      if (full !== '') return full;
      if (nickname !== '') return nickname;
      return user.username;
  }
}

// MARK: - The reader's current policy

let _current: Format = 'unresolved';

/**
 * Written by the sync engine (server default + lock + preference, resolved), read by every
 * derivation that names a person. (Swift guards it with a lock because reloads run off-main;
 * here the worker and the UI each hold their own copy, so the sync engine must push the resolved
 * value to whichever thread derives names.)
 */
export function current(): Format {
  return _current;
}

export function setCurrent(format: Format): void {
  _current = format;
}

/** Namespace form, for `TeammateNameDisplay.name(...)` call sites (the sibling modules' style). */
export const TeammateNameDisplay = { parse, resolveFormat, fullName, name, current, setCurrent } as const;
